<template>
    <div class="p-4 max-w-6xl mx-auto">
        <div class="mb-4">
            <div class="flex justify-between items-center mb-2">
                <h1 class="text-2xl font-semibold">Inventory by Person</h1>
                <label class="flex items-center text-sm">
                    <input type="checkbox" v-model="offboardingMode" class="mr-1" />
                    Offboarding mode
                </label>
            </div>
            <div class="flex space-x-2 items-center flex-wrap gap-y-2">
                <select v-model="selectedUserId" @change="loadSelection" class="border rounded p-2 min-w-[18rem]">
                    <option :value="'all'">All (every person)</option>
                    <option :value="null">Select a person…</option>
                    <option v-for="u in userOptions" :key="u.id" :value="u.id">
                        {{ u.first_name }} {{ u.last_name }} ({{ u.username }})
                    </option>
                </select>
                <button
                    type="button"
                    @click="laptopsOnly = !laptopsOnly"
                    :class="laptopsOnly ? 'bg-dark-primary text-white hover:bg-dark-secondary' : 'bg-gray-100 hover:bg-gray-200'"
                    class="rounded px-3 py-1 text-sm border"
                    :title="laptopsOnly ? 'Showing laptops only — click to show every category' : 'Showing every category — click to show laptops only'"
                >
                    {{ laptopsOnly ? 'Laptops only' : 'All categories' }}
                </button>
                <button v-if="reports.length && offboardingMode" @click="printReport" class="rounded px-3 py-1 text-sm border hover:bg-gray-50">Print turn-in summary</button>
            </div>
        </div>

        <div v-if="loading" class="text-sm text-gray-500 italic">
            Loading{{ loadProgress.total > 0 ? ` (${loadProgress.done} of ${loadProgress.total})` : '' }}…
        </div>

        <div v-else-if="reports.length === 0 && selectedUserId !== null" class="text-sm text-gray-500 italic">
            No data to display.
        </div>

        <div v-else id="by-person-report">
            <template v-for="r in filteredReports" :key="`person-${r.user.id}`">
                <div v-if="hasContentForReport(r)" class="border rounded mb-6 overflow-hidden">
                    <div class="bg-gray-50 border-b p-3 flex justify-between items-center">
                        <div>
                            <div class="text-lg font-semibold">{{ r.user.first_name }} {{ r.user.last_name }}</div>
                            <div class="text-xs text-gray-600">{{ r.user.email }} · @{{ r.user.username }}</div>
                        </div>
                        <div class="text-right">
                            <div class="text-sm">
                                <strong>Currently held{{ laptopsOnly ? ' (laptops)' : '' }}:</strong>
                                {{ r.filteredCurrent.length }} item(s)
                            </div>
                            <div class="text-xs text-gray-600">
                                Total cost in possession: ${{ formatNumber(sumCost(r.filteredCurrent)) }}
                            </div>
                        </div>
                    </div>

                    <div class="p-3">
                        <div v-if="offboardingMode && r.filteredCurrent.length" class="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
                            <div class="text-sm font-semibold mb-1">
                                Turn-in checklist: {{ collectedCountFor(r) }} of {{ r.filteredCurrent.length }} collected
                            </div>
                            <div class="text-xs text-gray-700">Record a return on each item below. Once all show as collected, the person has fully turned in their kit.</div>
                        </div>

                        <h2 class="text-base font-semibold mb-2">Currently held</h2>
                        <table v-if="r.filteredCurrent.length" class="w-full text-sm mb-6 border-collapse table-fixed">
                            <colgroup>
                                <col class="w-32" />
                                <col />
                                <col class="w-44" />
                                <col class="w-28" />
                                <col class="w-32" />
                                <col v-if="offboardingMode" class="w-32" />
                            </colgroup>
                            <thead class="text-left text-xs uppercase text-gray-500 border-b">
                                <tr>
                                    <th class="py-2">Category</th>
                                    <th class="py-2">Item</th>
                                    <th class="py-2">Asset / Serial</th>
                                    <th class="py-2">Issued</th>
                                    <th class="py-2">Condition</th>
                                    <th v-if="offboardingMode" class="py-2">Turn-in</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in r.filteredCurrent" :key="item.id" class="border-b align-top">
                                    <td class="py-2">{{ item.category?.category_name || '—' }}</td>
                                    <td class="py-2 pr-2">
                                        <div class="font-medium">{{ item.brand }} {{ item.model }}</div>
                                        <div class="text-xs text-gray-500">{{ item.item_name || '' }}</div>
                                    </td>
                                    <td class="py-2">
                                        <div class="font-mono">{{ item.asset_name || '—' }}</div>
                                        <div class="text-xs text-gray-500 font-mono">SN: {{ item.serial_number || '—' }}</div>
                                    </td>
                                    <td class="py-2">{{ formatDate(item.current_assignment?.issued_on) }}</td>
                                    <td class="py-2">{{ item.current_assignment?.condition_at_issue || '—' }}</td>
                                    <td v-if="offboardingMode" class="py-2">
                                        <span v-if="collected[item.id]" class="text-green-700 text-xs">✓ collected</span>
                                        <button v-else @click="openReturnFor(item)" class="text-xs rounded px-2 py-0.5 bg-dark-primary text-white hover:bg-dark-secondary">Record return</button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-else class="text-sm text-gray-500 italic mb-6">
                            {{ laptopsOnly ? 'No laptops currently held.' : 'Nothing currently held.' }}
                        </div>

                        <h2 class="text-base font-semibold mb-2">History (previously held)</h2>
                        <table v-if="r.filteredHistory.length" class="w-full text-sm border-collapse table-fixed">
                            <colgroup>
                                <col class="w-32" />
                                <col />
                                <col class="w-44" />
                                <col class="w-28" />
                                <col class="w-32" />
                            </colgroup>
                            <thead class="text-left text-xs uppercase text-gray-500 border-b">
                                <tr>
                                    <th class="py-2">Category</th>
                                    <th class="py-2">Item</th>
                                    <th class="py-2">Asset / Serial</th>
                                    <th class="py-2">Returned</th>
                                    <th class="py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in r.filteredHistory" :key="item.id" class="border-b align-top">
                                    <td class="py-2">{{ item.category?.category_name || '—' }}</td>
                                    <td class="py-2 pr-2">
                                        <div class="font-medium">{{ item.brand }} {{ item.model }}</div>
                                    </td>
                                    <td class="py-2">
                                        <div class="font-mono">{{ item.asset_name || '—' }}</div>
                                        <div class="text-xs text-gray-500 font-mono">SN: {{ item.serial_number || '—' }}</div>
                                    </td>
                                    <td class="py-2">{{ mostRecentReturnFor(item) }}</td>
                                    <td class="py-2"></td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-else class="text-sm text-gray-500 italic">
                            {{ laptopsOnly ? 'No laptop history on record.' : 'No prior assignments on record.' }}
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <base-modal-small v-if="returnDialogItem" @close="returnDialogItem = null">
            <template #header>Record return for {{ returnDialogItem.model }}</template>
            <template #body>
                <div class="space-y-2 text-sm p-2">
                    <div class="flex flex-col">
                        <label class="text-gray-500">Return date</label>
                        <input type="date" v-model="returnForm.returned_on" class="border rounded p-1" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Condition at return</label>
                        <input type="text" v-model="returnForm.condition_at_return" class="border rounded p-1" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Return notes</label>
                        <textarea v-model="returnForm.return_notes" class="border rounded p-1" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">After-photo (optional, camera on touch)</label>
                        <input type="file" accept="image/*" capture="environment" @change="onPhotoSelected" />
                    </div>
                    <div class="flex justify-end space-x-2 pt-2">
                        <button type="button" @click="returnDialogItem = null" class="px-3 py-1 border rounded">Cancel</button>
                        <button type="button" :disabled="busy" @click="submitReturn" class="px-3 py-1 rounded bg-dark-primary text-white">Record return</button>
                    </div>
                </div>
            </template>
        </base-modal-small>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import { formatDate } from '@/composables/formatDate';
import { getUsers } from '@/services/User';
import {
    getInventoryByPerson,
    getInventoryByAllPeople,
    recordInventoryReturn,
    uploadAssignmentPhoto,
} from '@/services/Inventory';

const props = defineProps(['userId']);
const store = useStore();
const router = useRouter();
const $toast = useToast();

const userOptions = ref([]);
// selectedUserId: a numeric user pk, or the string 'all', or null.
// Default to 'all' so the page is useful immediately on first open.
const selectedUserId = ref('all');
// reports: an array of person-report objects. Single-person view = one
// element; "All" view = one per user.
const reports = ref([]);
const loading = ref(false);
const loadProgress = ref({ done: 0, total: 0 });
const busy = ref(false);
const offboardingMode = ref(false);
const laptopsOnly = ref(true); // default: focus on laptops
const collected = ref({}); // item.id -> true

const returnDialogItem = ref(null);
const returnForm = ref({ returned_on: '', condition_at_return: '', return_notes: '', photoFile: null });

function isLaptop(item) {
    const name = (item?.category?.category_name || '').toLowerCase();
    return name === 'laptop';
}

const filteredReports = computed(() => {
    return reports.value.map(r => ({
        ...r,
        filteredCurrent: laptopsOnly.value ? r.current.filter(isLaptop) : r.current,
        filteredHistory: laptopsOnly.value ? r.history.filter(isLaptop) : r.history,
    }));
});

function hasContentForReport(r) {
    // In "All" mode, hide people who have nothing in the active filter view.
    if (reports.value.length <= 1) return true;
    return r.filteredCurrent.length > 0 || r.filteredHistory.length > 0;
}

function collectedCountFor(r) {
    return r.filteredCurrent.reduce(
        (n, item) => n + (collected.value[item.id] ? 1 : 0),
        0,
    );
}

function sumCost(items) {
    return items.reduce((sum, item) => sum + Number(item.cost || 0), 0);
}

async function loadUsers() {
    try {
        const result = await getUsers({ endpoint: '', token: store.state.auth.token });
        userOptions.value = (result.users || [])
            .filter(u => u.is_active && u.has_groups && !u.hide_from_people_pickers)
            .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' }));
    } catch (e) {
        $toast.error(`Couldn't load users: ${describe(e)}`);
    }
}

async function loadPersonReport(userId) {
    const resp = await getInventoryByPerson(userId, store.state.auth.token);
    return resp.data;
}

async function loadSelection() {
    if (selectedUserId.value === null) {
        reports.value = [];
        return;
    }
    loading.value = true;
    try {
        if (selectedUserId.value === 'all') {
            // Single bulk request — backend returns the full per-person bundle
            // in one round-trip with a small fixed number of queries.
            const resp = await getInventoryByAllPeople(store.state.auth.token);
            reports.value = resp.data || [];
            router.replace({ name: 'inventory-by-person' });
        } else {
            const r = await loadPersonReport(selectedUserId.value);
            reports.value = [r];
            router.replace({ name: 'inventory-by-person', params: { userId: selectedUserId.value } });
        }
        collected.value = {};
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        loading.value = false;
        loadProgress.value = { done: 0, total: 0 };
    }
}

onMounted(async () => {
    await loadUsers();
    if (props.userId) {
        selectedUserId.value = Number(props.userId);
    }
    await loadSelection();
});

watch(() => props.userId, async (val) => {
    if (val && Number(val) !== selectedUserId.value) {
        selectedUserId.value = Number(val);
        await loadSelection();
    }
});

function openReturnFor(item) {
    returnDialogItem.value = item;
    returnForm.value = {
        returned_on: new Date().toISOString().slice(0, 10),
        condition_at_return: item.current_assignment?.condition_at_issue || '',
        return_notes: '',
        photoFile: null,
    };
}

function onPhotoSelected(e) {
    returnForm.value.photoFile = e.target.files[0] || null;
}

async function submitReturn() {
    busy.value = true;
    try {
        const token = store.state.auth.token;
        const { photoFile, ...payload } = returnForm.value;
        const resp = await recordInventoryReturn(returnDialogItem.value.id, payload, token);
        const assignment = resp.data;
        if (photoFile) {
            try {
                await uploadAssignmentPhoto({ assignmentId: assignment.id, phase: 'return', file: photoFile }, token);
            } catch (e) {
                $toast.warning(`Return recorded, but photo upload failed: ${describe(e)}`);
            }
        }
        collected.value[returnDialogItem.value.id] = true;
        $toast.success(`Return recorded for ${returnDialogItem.value.model}.`);
        returnDialogItem.value = null;
        await loadSelection();
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        busy.value = false;
    }
}

function mostRecentReturnFor(item) {
    const returns = (item.inventory_users || []).filter(u => u.returned_on);
    if (!returns.length) return '—';
    return formatDate(returns[0].returned_on);
}

function formatNumber(n) {
    if (n == null) return '0';
    return Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function printReport() {
    window.print();
}

function describe(e) {
    if (e?.response?.data) {
        if (typeof e.response.data === 'string') return e.response.data;
        return Object.values(e.response.data).flat().join(' · ');
    }
    return e?.message || String(e);
}
</script>

<style scoped>
@media print {
    button, input[type="checkbox"], select { display: none; }
}
</style>
